import { Controller } from "@hotwired/stimulus"
import Gantt from "frappe-gantt"

// Mounts frappe-gantt on the project timeline. The server is the source of
// truth: dragging or resizing a bar PATCHes its reschedule URL (a list bar
// changes the phase dates, a to-do bar the to-do dates) and redraws with
// the rows that come back, so a derived list bar realigns too.
export default class extends Controller {
  static targets = ["chart", "viewToggle"]
  static values = {
    tasks: Array,
    mode: String,
    openLabel: String,
    editLabel: String,
    phaseLabel: String
  }

  connect() {
    this.tasks = this.tasksValue
    this.viewMode = "Day"
    this.gantt = new Gantt(this.chartTarget, this.tasks, {
      view_mode: "Day",
      view_mode_select: false,
      language: document.documentElement.lang || "it",
      readonly_progress: true,
      snap_at: "1d",
      infinite_padding: false,
      popup: this.popup.bind(this),
      on_date_change: this.onDateChange.bind(this)
    })
  }

  disconnect() {
    this.gantt = null
    this.chartTarget.innerHTML = ""
  }

  setView(event) {
    const view = event.params.view
    this.viewMode = view
    this.gantt.change_view_mode(view)
    for (const button of this.viewToggleTarget.querySelectorAll(".gantt-view-btn")) {
      button.classList.toggle("is-active", button.dataset.ganttViewParam === view)
    }
  }

  async onDateChange(task, start, end) {
    if (!task.reschedule_url) return this.refresh()

    const startsOn = this.isoDate(start)
    const dueOn = this.isoDate(new Date(end - 1000))
    const key = task.kind === "list" ? "todo_list" : "todo"

    try {
      const response = await fetch(`${task.reschedule_url}?mode=${this.modeValue}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content
        },
        body: JSON.stringify({ [key]: { starts_on: startsOn, due_on: dueOn } })
      })
      if (response.ok) {
        const data = await response.json()
        this.tasks = data.tasks
      }
    } catch {
      // network down: this.tasks stays the last known truth, refresh restores it
    }
    this.refresh()
  }

  refresh() {
    if (!this.gantt) return
    this.gantt.refresh(this.tasks)
  }

  popup(ctx) {
    const task = ctx.task
    return task.kind === "list" ? this.listPopup(ctx, task) : this.todoPopup(ctx, task)
  }

  listPopup(ctx, task) {
    ctx.set_title(task.name)
    ctx.set_subtitle(this.phaseLabelValue)
    const lastDay = new Date(task._end - 1000)
    const days = this.daysBetween(this.dateOnly(task._start), this.dateOnly(lastDay)) + 1
    ctx.set_details(`${this.formatDate(task._start)} → ${this.formatDate(lastDay)} (${days}g) · ${task.progress}% · ${task.done_count}/${task.done_count + task.open_count}`)
  }

  // Strings interpolated here arrive HTML-escaped from the server
  // (Timeline#todo_row), so they are safe for frappe-gantt's innerHTML.
  todoPopup(ctx, task) {
    ctx.set_title(task.name)
    ctx.set_subtitle("")
    const lastDay = new Date(task._end - 1000)
    const days = this.daysBetween(this.dateOnly(task._start), this.dateOnly(lastDay)) + 1

    const meta = task.list_name
      ? `<div class="flex items-center gap-2 text-xs text-muted mt-1"><span class="inline-block w-2 h-2 rounded-full" style="background:${task.color}"></span>${task.list_name}</div>`
      : ""
    const assignees = task.assignees && task.assignees.length
      ? `<div class="text-xs text-muted mt-1">${task.assignees.join(", ")}</div>`
      : ""
    const dateLine = `<div class="text-xs text-muted mt-2">${this.formatDate(task._start)} → ${this.formatDate(lastDay)} (${days}g)</div>`
    const actions = `<div class="flex items-center gap-3 mt-2 pt-2 border-t border-border">
      <a href="${task.url}" data-turbo-frame="drawer_frame" data-action="drawer#open" data-drawer-url-value="${task.url}" class="text-xs font-medium underline">${this.editLabelValue}</a>
      <a href="${task.url}" class="text-xs font-medium underline">${this.openLabelValue}</a>
    </div>`

    ctx.set_details(`${meta}${assignees}${dateLine}${actions}`)
  }

  dateOnly(date) {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate())
  }

  daysBetween(from, to) {
    return Math.round((to - from) / 86400000)
  }

  isoDate(date) {
    const month = String(date.getMonth() + 1).padStart(2, "0")
    const day = String(date.getDate()).padStart(2, "0")
    return `${date.getFullYear()}-${month}-${day}`
  }

  formatDate(date) {
    return date.toLocaleDateString(document.documentElement.lang || "it", { day: "numeric", month: "short" })
  }
}
